// Open-time framing for the pedigree canvas (TGCLOUD-1863).
//
// Pure: no d3, no DOM. Unit-tested in openFrame.test.js; Canvas.jsx and zoom.js
// read it. Three decisions live here so that the canvas (which jsdom cannot
// mount) carries none of them:
//
//  • focusPointFor — WHOM to centre on when a pedigree opens: the person the
//    clinician arrived for (Search Family Records in focus, a Data Explorer row,
//    a lab report link, a sidebar link, a person tab), else the ARROWED proband
//    (probandState 'explicit' / 'legacy'), else the structural root when the
//    arrow has been cleared. Before this the open centred on the structural root
//    whatever the arrow said, so Make Proband moved the arrow and not the view.
//
//  • shouldFrame — WHETHER a render frames the view. Never on an empty layout:
//    the canvas mounts under the family fetch with an empty member list, and
//    laying THAT out rendered an empty chart, framed it with the legacy top-left
//    fallback and latched "first open", so the real layout then arrived as a
//    re-render and restored a view with the proband off-screen (Kathryn's
//    44-person family). The first non-empty layout frames; so does every later
//    relayout of the open (label presets, legend maps, auto-spacing settle) until
//    the clinician has TOUCHED the view — a pan or wheel, the +/- buttons, a
//    locate, a click on a node, a drag, an edit. From then on the live view is
//    restored exactly as before, which keeps the July rule intact: after an add
//    the EDITED node stays fixed on screen; the proband is only ever the anchor
//    when no node is active (PLAN-pedigree-live-edit-stability.md, Issue 4).
//
//  • visibleCenter — WHERE the centre of the uncovered canvas is. The detail
//    drawer floats over the right of a full-width canvas, and a person opened in
//    focus opens that drawer in the same effect that selects them, so centring on
//    the full width would seat them at the drawer's edge.

const finitePoint = (n) => !!n && Number.isFinite(n.x) && Number.isFinite(n.y);

const sameId = (node, id) =>
  node != null &&
  id != null &&
  (String(node.pid) === String(id) || String(node.uid) === String(id));

/**
 * The layout point to centre the open on.
 *
 * @param {Array<object>} nodes  the laid-out render nodes ({ pid, uid, x, y, proBandId … })
 * @param {object} [opts]
 * @param {string|number|null} [opts.focusId]  pid or uid of the person in context, if any
 * @param {{ probandPid?: number|string|null }|null} [opts.probandState]  getProbandState(members)
 * @returns {{x:number, y:number}|null}  null when nothing can be centred on (an empty layout)
 */
export const focusPointFor = (
  nodes,
  { focusId = null, probandState = null } = {}
) => {
  const list = Array.isArray(nodes) ? nodes : [];
  const point = (n) => ({ x: n.x, y: n.y });

  if (focusId != null) {
    const focus = list.find((n) => sameId(n, focusId));
    if (finitePoint(focus)) return point(focus);
  }
  const probandPid = probandState?.probandPid;
  if (probandPid != null) {
    const arrowed = list.find((n) => n && String(n.pid) === String(probandPid));
    if (finitePoint(arrowed)) return point(arrowed);
  }
  const root = list.find((n) => n && n.proBandId);
  return finitePoint(root) ? point(root) : null;
};

/**
 * Whether this render re-frames the view on the focus point.
 *
 * @param {object} [state]
 * @param {number} [state.nodeCount]  nodes in the layout being rendered
 * @param {boolean} [state.framed]    a non-empty layout of this pedigree has been framed already
 * @param {boolean} [state.touched]   the clinician has interacted with the view since the open
 */
export const shouldFrame = ({
  nodeCount = 0,
  framed = false,
  touched = false
} = {}) => {
  if (!(nodeCount > 0)) return false;
  if (!framed) return true;
  return !touched;
};

/**
 * The screen point to centre on: the middle of the canvas not covered by the
 * drawer. An inset that is not a sensible width (missing, non-finite, or the
 * whole canvas) centres on the full width.
 */
export const visibleCenter = (vw, vh, inset = 0) => {
  const i =
    Number.isFinite(inset) && inset > 0 && Number.isFinite(vw) && inset < vw
      ? inset
      : 0;
  return { x: (vw - i) / 2, y: vh / 2 };
};
